import { useEffect, useMemo, useState, useRef } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import api from "../lib/api";

/**
 * Searchable country picker — fetches /api/countries once, filters client-side.
 * Props:
 *   value: ISO-2 country code (e.g. "NG"), or null
 *   onChange: (code, country) => void  — country is {code, name, dial, region}
 *   placeholder?: string
 *   testId?: string
 */
export default function CountrySelect({ value, onChange, placeholder = "Select your country", testId = "country-select" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [countries, setCountries] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/countries");
        if (!cancelled) setCountries(data.countries || []);
      } catch (e) {
        if (!cancelled) setCountries([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selected = useMemo(() => countries.find((c) => c.code === value) || null, [countries, value]);

  const filtered = useMemo(() => {
    if (!query.trim()) return countries;
    const q = query.toLowerCase();
    return countries.filter((c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase() === q);
  }, [countries, query]);

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 bg-[#0E0E0E] border border-[#262626] hover:border-[#D4AF37]/40 text-[#F5F5F5] text-sm px-4 py-3 rounded-sm transition-colors"
        data-testid={`${testId}-trigger`}
      >
        <span className={selected ? "text-[#F5F5F5]" : "text-[#888]"}>
          {selected ? `${selected.name} (${selected.dial})` : placeholder}
        </span>
        <ChevronDown size={16} className={`text-[#888] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1.5 bg-[#0E0E0E] border border-[#262626] rounded-sm shadow-[0_20px_50px_-10px_rgba(0,0,0,0.8)] max-h-[300px] flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#1A1A1A]">
            <Search size={14} className="text-[#666]" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country…"
              className="flex-1 bg-transparent text-sm text-[#F5F5F5] placeholder-[#666] outline-none"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-[#666]">No country matches "{query}"</div>
            )}
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => { onChange(c.code, c); setOpen(false); setQuery(""); }}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-[#181207] transition-colors ${value === c.code ? "bg-[#181207]" : ""}`}
                data-testid={`${testId}-option-${c.code}`}
              >
                <span className="text-[#E5E5E5]">{c.name}</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-[#888]">{c.dial}</span>
                  {value === c.code && <Check size={14} className="text-[#D4AF37]" />}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
